import React, { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import ShieldImg from "../../Assets/Icons/shield.png";

const AdminAddTeams = () => {
  const groups = ["A", "B", "C", "D", "E", "F", "G", "H"];
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTeamsVisible, setIsTeamsVisible] = useState(false);
  const [teams, setTeams] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // State za kreiranje novog tima
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamLogo, setNewTeamLogo] = useState(null);

  // State za uređivanje tima i igrača
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamName, setEditTeamName] = useState("");
  const [editTeamLogo, setEditTeamLogo] = useState(null);
  const [playerInput, setPlayerInput] = useState({ no: "", name: "" });
  const [editingPlayerIndex, setEditingPlayerIndex] = useState(null);

  const fetchTeams = async () => {
    // 1. Dohvati sve timove
    const { data: teamsData, error: teamsError } = await supabase
      .from("teams")
      .select("*");
    // 2. Dohvati sve dovršene utakmice
    const { data: matchesData, error: matchesError } = await supabase
      .from("matches")
      .select("*")
      .eq("is_completed", true);

    if (teamsError || matchesError) {
      console.error("Greška:", teamsError || matchesError);
      return;
    }

    // Inicijaliziraj stats objekt
    const stats = {};
    teamsData.forEach((t) => {
      stats[t.id] = {
        ...t,
        played: 0,
        points: 0,
        goals_for: 0,
        goals_against: 0,
      };
    });

    // Izračunaj statistiku iz utakmica
    matchesData.forEach((m) => {
      const tA = stats[m.team_a_id];
      const tB = stats[m.team_b_id];
      if (tA && tB) {
        tA.played += 1;
        tB.played += 1;
        tA.goals_for += m.scorea;
        tA.goals_against += m.scoreb;
        tB.goals_for += m.scoreb;
        tB.goals_against += m.scorea;
        if (m.scorea > m.scoreb) tA.points += 3;
        else if (m.scorea < m.scoreb) tB.points += 3;
        else {
          tA.points += 1;
          tB.points += 1;
        }
      }
    });

    // Sortiraj i postavi u state
    const processedTeams = Object.values(stats).sort(
      (a, b) => b.points - a.points || b.goals_for - a.goals_for,
    );
    setTeams(processedTeams);
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (editingTeam) {
      const current = teams.find((t) => t.id === editingTeam.id);

      if (current && JSON.stringify(current) !== JSON.stringify(editingTeam)) {
        setEditingTeam(current);
      }
    }
  }, [teams, editingTeam]);

  // U useEffect koji već imaš ili novi:
  useEffect(() => {
    fetchTeams();
    // Dohvat postavke vidljivosti
    supabase
      .from("site_settings")
      .select("prikazi_timove")
      .eq("id", 1)
      .single()
      .then(({ data }) => {
        if (data) setIsTeamsVisible(data.prikazi_timove);
      });
  }, []);

  // Funkcija za toggle
  const handleToggleVisibility = async (newValue) => {
    setIsTeamsVisible(newValue);
    await supabase
      .from("site_settings")
      .update({ prikazi_timove: newValue })
      .eq("id", 1);
  };

  const uploadLogo = async (file) => {
    const compressedFile = await compressImage(file);

    // 2. Postavi ekstenziju na .webp jer kompresor uvijek vraća webp
    const fileName = `${Math.random()}-${Date.now()}.webp`;
    const filePath = `team-logos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("logos")
      .upload(filePath, compressedFile); // <-- Šaljemo smanjeni 'compressedFile'

    if (uploadError) throw uploadError;
    const { data } = supabase.storage.from("logos").getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleSaveNewTeam = async () => {
    if (!newTeamName) return alert("Upišite ime tima!");
    setIsUploading(true);
    try {
      let finalLogoUrl = null;
      if (newTeamLogo) {
        finalLogoUrl = await uploadLogo(newTeamLogo);
      }

      await supabase.from("teams").insert([
        {
          name: newTeamName,
          group_name: selectedGroup,
          logo_url: finalLogoUrl,
          players: [],
          points: 0,
          goals_for: 0,
          goals_against: 0,
          played: 0,
        },
      ]);

      setIsTeamModalOpen(false);
      setNewTeamName("");
      setNewTeamLogo(null);
      fetchTeams();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const openEditModal = (team) => {
    setEditingTeam(team);
    setEditTeamName(team.name);
    setEditTeamLogo(team.logo_url);
    setPlayerInput({ no: "", name: "" });
    setEditingPlayerIndex(null);
    setIsEditModalOpen(true);
  };

  // ========================================================
  // 🔥 AUTOMATSKO SMANJIVANJE I KOMPRESIJU GRBOVA TIMOVA
  // ========================================================
  const compressImage = (file, maxWidth = 300, maxHeight = 300) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], `${Date.now()}.webp`, {
                  type: "image/webp",
                });
                resolve(compressedFile);
              } else {
                reject(new Error("Kompresija nije uspjela."));
              }
            },
            "image/webp",
            0.8,
          );
        };
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleUpdateTeamData = async () => {
    if (!editTeamName) return alert("Ime tima ne može biti prazno!");
    setIsUploading(true);
    try {
      let finalLogoUrl = editingTeam.logo_url;
      if (editTeamLogo && typeof editTeamLogo !== "string") {
        finalLogoUrl = await uploadLogo(editTeamLogo);
      }

      const { error } = await supabase
        .from("teams")
        .update({ name: editTeamName, logo_url: finalLogoUrl })
        .eq("id", editingTeam.id);

      if (error) throw error;
      alert("Uspješno spremljeno!");
      fetchTeams();
    } catch (err) {
      alert(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSavePlayer = async () => {
    if (!playerInput.name || !playerInput.no)
      return alert("Popunite polja za igrača!");

    let updatedPlayers = [...(editingTeam.players || [])];

    if (editingPlayerIndex !== null) {
      updatedPlayers[editingPlayerIndex] = {
        ...updatedPlayers[editingPlayerIndex],
        name: playerInput.name,
        no: parseInt(playerInput.no),
      };
    } else {
      updatedPlayers.push({
        name: playerInput.name,
        no: parseInt(playerInput.no),
        goals: 0,
      });
    }

    const { error } = await supabase
      .from("teams")
      .update({ players: updatedPlayers })
      .eq("id", editingTeam.id);

    if (!error) {
      setPlayerInput({ no: "", name: "" });
      setEditingPlayerIndex(null);
      fetchTeams();
    } else {
      alert("Greška: " + error.message);
    }
  };

  const handleDeletePlayer = async (index) => {
    if (!window.confirm("Obrisati igrača?")) return;
    let updatedPlayers = [...(editingTeam.players || [])];
    updatedPlayers.splice(index, 1);

    const { error } = await supabase
      .from("teams")
      .update({ players: updatedPlayers })
      .eq("id", editingTeam.id);

    if (!error) {
      fetchTeams();
    } else {
      alert("Greška: " + error.message);
    }
  };

  const handleDeleteTeam = async (id) => {
    if (window.confirm("Obrisati cijeli tim i sve igrače?")) {
      await supabase.from("teams").delete().eq("id", id);
      setIsEditModalOpen(false);
      setEditingTeam(null);
      fetchTeams();
    }
  };

  const groupTeams = teams.filter((t) => t.group_name === selectedGroup);

  return (
    <div className="admin-teams-container">
      <div className="visibility-control-bar" style={{ marginBottom: "20px" }}>
        <span className="control-label">Prikaži timove na webu</span>
        <label className="ios-switch">
          <input
            type="checkbox"
            checked={isTeamsVisible}
            onChange={(e) => handleToggleVisibility(e.target.checked)}
          />
          <span className="slider"></span>
        </label>
      </div>

      {!selectedGroup ? (
        <div className="groups-grid">
          {groups.map((g) => {
            const count = teams.filter((t) => t.group_name === g).length;
            return (
              <div
                key={g}
                className="group-card"
                onClick={() => setSelectedGroup(g)}
              >
                <div className="group-card-badge">GRUPA</div>
                <h3>{g}</h3>
                <span className="team-count">
                  <i className="fi fi-rs-users-alt"></i> Broj ekipa:{" "}
                  <strong>{count}</strong>
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="group-detail">
          <button
            className="back-btn-simple"
            onClick={() => setSelectedGroup(null)}
          >
            <i className="fi fi-rs-angle-left"></i> Popis grupa
          </button>

          <div className="header-row">
            <h3>GRUPA {selectedGroup}</h3>
            <button
              className="add-btn-main"
              onClick={() => setIsTeamModalOpen(true)}
            >
              <i className="fi fi-rs-plus-small"></i> DODAJ TIM
            </button>
          </div>

          {groupTeams.length === 0 ? (
            <div className="no-teams-placeholder">
              <i className="fi fi-rs-shield-check"></i>
              <p>Još nema dodanih timova u ovoj grupi.</p>
              {/*         <button
                className="add-btn-main"
                onClick={() => setIsTeamModalOpen(true)}
              >
                Dodaj prvi tim
              </button> */}
            </div>
          ) : (
            <div className="table-responsive-wrapper">
              <table className="teams-table-preview">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Ekipa</th>
                    <th className="text-center">UT</th>
                    <th className="text-center">GR</th>
                    <th className="text-center">BOD</th>
                    <th className="text-right">Akcija</th>
                  </tr>
                </thead>
                <tbody>
                  {groupTeams.map((team, index) => {
                    const goalDiff = team.goals_for - team.goals_against;
                    return (
                      <tr key={team.id}>
                        <td className="team-position">{index + 1}.</td>
                        <td>
                          <div
                            className="team-table-identity"
                            onClick={() => openEditModal(team)}
                          >
                            <img src={team.logo_url || ShieldImg} alt="logo" />
                            <span className="team-name-txt">{team.name}</span>
                          </div>
                        </td>
                        <td className="text-center text-muted-table">
                          {team.played}
                        </td>
                        <td className="text-center text-muted-table">
                          {goalDiff > 0 ? `+${goalDiff}` : goalDiff}
                        </td>
                        <td className="text-center team-points-cell">
                          {team.points}
                        </td>
                        <td className="text-right">
                          <button
                            onClick={() => openEditModal(team)}
                            className="action-edit-btn"
                          >
                            <i className="fi fi-rs-settings"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MODAL ZA DODAVANJE NOVOG TIMA */}
      {isTeamModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsTeamModalOpen(false)}
        >
          <div
            className="event-modal animate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-container">
              <h3>Novi Tim (Grupa {selectedGroup})</h3>
              <i
                className="fi fi-rs-cross close-modal-icon"
                onClick={() => setIsTeamModalOpen(false)}
              ></i>
            </div>

            <div className="form-group">
              <label>Naziv Tima</label>
              <input
                type="text"
                placeholder="Unesi ime ekipe..."
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Grb / Logo</label>
              <label htmlFor="new-logo-upload" className="custom-file-upload">
                {newTeamLogo ? (
                  <div className="logo-preview-badge">
                    <i
                      className="fi fi-rs-check-circle"
                      style={{ color: "#2ecc71" }}
                    ></i>
                    <span>Slika uspješno dodana</span>
                  </div>
                ) : (
                  <>
                    <i className="fi fi-rs-plus-small"></i> Dodaj sliku
                  </>
                )}
              </label>
              <input
                id="new-logo-upload"
                type="file"
                accept="image/*"
                onChange={(e) => setNewTeamLogo(e.target.files[0])}
                style={{ display: "none" }}
              />
            </div>
            <button
              className="add-btn"
              onClick={handleSaveNewTeam}
              disabled={isUploading}
            >
              {isUploading ? "Spremanje..." : "SPREMI TIM"}
            </button>
          </div>
        </div>
      )}

      {/* MODAL ZA UREĐIVANJE TIMA */}
      {isEditModalOpen && editingTeam && (
        <div
          className="modal-overlay"
          onClick={() => {
            setIsEditModalOpen(false);
            setEditingTeam(null);
          }}
        >
          <div
            className="event-modal edit-team-modern-modal animate-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-container">
              <div className="modal-title-with-img">
                <img
                  src={editingTeam.logo_url || ShieldImg}
                  alt="logo-preview"
                />
                <h3>Uredi tim: {editingTeam.name}</h3>
              </div>
              <i
                className="fi fi-rs-cross close-modal-icon"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingTeam(null);
                }}
              ></i>
            </div>

            <div className="modal-scrollable-content">
              {/* STATISTIKA (ZAKLJUČANO ZA MIJENJANJE) */}
              <div className="stats-readonly-grid">
                <div className="s-item">
                  <span>Odigrano</span>
                  <strong>{editingTeam.played}</strong>
                </div>
                <div className="s-item">
                  <span>GR</span>
                  <strong>
                    {editingTeam.goals_for - editingTeam.goals_against}
                  </strong>
                </div>
                <div className="s-item accent">
                  <span>Bodovi</span>
                  <strong>{editingTeam.points}</strong>
                </div>
                <div className="s-item">
                  <span>Grupa</span>
                  <strong>{editingTeam.group_name}</strong>
                </div>
              </div>

              {/* PODACI O KLUBU */}
              <div className="modern-section-box">
                <h4>
                  <i className="fi fi-rs-shield"></i> Informacije o klubu
                </h4>
                <div className="form-group-row">
                  <div className="form-group flex-2">
                    <label>Naziv ekipe</label>
                    <input
                      type="text"
                      value={editTeamName}
                      onChange={(e) => setEditTeamName(e.target.value)}
                    />
                  </div>
                  <div className="form-group flex-1">
                    <label>Grb kluba</label>
                    <label
                      htmlFor="edit-logo-upload"
                      className="custom-file-upload"
                    >
                      {editTeamLogo && typeof editTeamLogo === "string" ? (
                        <div className="logo-preview-badge">
                          <img src={editTeamLogo} alt="Preview" />
                          <span>Promijeni sliku</span>
                        </div>
                      ) : editTeamLogo && typeof editTeamLogo === "object" ? (
                        <div className="logo-preview-badge">
                          <span>Odabrana nova slika</span>
                        </div>
                      ) : (
                        <>
                          <i className="fi fi-rs-plus-small"></i> Dodaj sliku
                        </>
                      )}
                    </label>
                    <input
                      id="edit-logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setEditTeamLogo(e.target.files[0])}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>
                <button
                  className="inline-update-btn"
                  onClick={handleUpdateTeamData}
                  disabled={isUploading}
                >
                  {isUploading ? "Spremanje..." : "Spremi promjene"}
                </button>
              </div>

              {/* SASTAV TIMA */}
              <div className="modern-section-box">
                <h4>
                  <i className="fi fi-rs-users"></i> Sastav tima (
                  {editingTeam.players?.length || 0})
                </h4>

                {/* Brzi unos */}
                <div className="player-fast-insert-form">
                  <input
                    type="number"
                    placeholder="Broj"
                    className="inp-no"
                    value={playerInput.no}
                    onChange={(e) =>
                      setPlayerInput({ ...playerInput, no: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="npr. K. Horvat"
                    className="inp-name"
                    value={playerInput.name}
                    onChange={(e) =>
                      setPlayerInput({ ...playerInput, name: e.target.value })
                    }
                  />
                  <button
                    className="btn-player-action"
                    onClick={handleSavePlayer}
                  >
                    {editingPlayerIndex !== null ? "Spremi" : "Dodaj"}
                  </button>
                  {editingPlayerIndex !== null && (
                    <button
                      className="btn-player-cancel"
                      onClick={() => {
                        setEditingPlayerIndex(null);
                        setPlayerInput({ no: "", name: "" });
                      }}
                    >
                      X
                    </button>
                  )}
                </div>

                {/* Popis igrača */}
                <div className="players-list-modern-wrapper">
                  {!editingTeam.players || editingTeam.players.length === 0 ? (
                    <p className="empty-players-label">
                      Nema dodanih igrača u timu.
                    </p>
                  ) : (
                    editingTeam.players.map((p, idx) => (
                      <div
                        key={idx}
                        className={`player-modern-row ${editingPlayerIndex === idx ? "under-edit" : ""}`}
                      >
                        <div className="p-info">
                          <span className="p-number">#{p.no}</span>
                          <span className="p-name">{p.name}</span>
                        </div>
                        <div className="p-actions">
                          <button
                            className="act-btn e"
                            onClick={() => {
                              setEditingPlayerIndex(idx);
                              setPlayerInput({
                                no: p.no.toString(),
                                name: p.name,
                              });
                            }}
                          >
                            <i className="fi fi-rs-pencil"></i>
                          </button>
                          <button
                            className="act-btn d"
                            onClick={() => handleDeletePlayer(idx)}
                          >
                            <i className="fi fi-rs-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* BRISANJE TIMA */}
              <div className="danger-zone-modern">
                <button
                  className="btn-delete-team-destructive"
                  onClick={() => handleDeleteTeam(editingTeam.id)}
                >
                  <i className="fi fi-rs-trash"></i> Obriši ovaj tim i sve
                  podatke
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAddTeams;
